import { Path, Editor, Node, Element, Transforms } from 'domains/reporter/RichTextEditor/core';
import clone from 'ramda/es/clone';
// NOTE:
// There's a nasty bug in slate-react that makes it to where inserting objects
// that share a reference borks Slate React. The only workaround I know of without redoing
// Slate React is to deep clone all of the nodes before inserting them into the editor.
// This is a super horrible hack and hits performance, but these functions aren't called a lot.
//
// Issue: https://github.com/ianstormtaylor/slate/issues/4309

import { MACRO_PLACEHOLDER_PLUGIN_ID } from './types';
import type { MacroPlaceholderPluginElement, Macro, MacroPlaceholderType } from './types';
import { PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import { unreachableCaseLog } from 'types';
import { replaceNodeAtPath } from '../../utils/replaceNodeAtPath';
import { walkSlateContent } from '../../utils/walkSlateContent';
import { isTextFragment, isInlineFragment } from '../../stitching';
import type { SlateContent } from '../../types';

type Nodes = any;
export type MacroNameLookupEntry = Readonly<{
  smid: string;
  name: string;
  nestedMacros?: ReadonlyArray<MacroNameLookupEntry> | null;
}>;

export type MacroLookupEntry = Readonly<{
  smid: string;
  name?: string;
  text?: Macro | string;
  nestedMacros?: ReadonlyArray<MacroLookupEntry> | null;
}>;

export const insertMacroPlaceholder = ({
  referenceID,
  displayName,
}: {
  referenceID: string;
  displayName: string;
}): MacroPlaceholderPluginElement => ({
  // Unique id used to associate the macro element with its metadata
  referenceID,
  type: MACRO_PLACEHOLDER_PLUGIN_ID,
  children: [{ text: displayName }],
});

export function isMacro(maybeMacro?: Macro | string): maybeMacro is Macro {
  return Array.isArray(maybeMacro) && maybeMacro.every((node) => Node.isNode(node));
}

export const normalizeMacro = (macro: Macro): Macro => {
  // PRD: If the first node in a macro is a paragraph, unwrap it so that those nodes will appear inline.
  if (macro[0].type === PARAGRAPH_PLUGIN_ID) {
    const newMacro = [...macro];
    newMacro.splice(0, 1);

    return [...macro[0].children, ...newMacro];
  }

  return macro;
};

export const getMacroEntry = (
  editor: Editor,
  maybeMacro: Macro | string
): [MacroPlaceholderType, Nodes] => {
  if (isMacro(maybeMacro)) {
    const normalizedMacro = normalizeMacro(maybeMacro as Macro);

    if (isTextFragment(editor, normalizedMacro)) {
      return ['macroText', normalizedMacro];
    } else {
      if (isInlineFragment(editor, normalizedMacro)) {
        return ['macroInline', normalizedMacro];
      } else {
        return ['macroBlock', normalizedMacro];
      }
    }
  } else {
    return ['plainText', maybeMacro];
  }
};

export const insertMacroAtPath = (editor: Editor, path: Path, maybeMacro: Macro | string): void => {
  const [macroType, normalizedMacro] = getMacroEntry(editor, maybeMacro);

  switch (macroType) {
    case 'plainText':
      replaceNodeAtPath(editor, [{ text: normalizedMacro }], { at: path });
      break;
    case 'macroText':
    case 'macroInline':
      replaceNodeAtPath(editor, normalizedMacro, { at: path });
      break;

    case 'macroBlock':
      replaceNodeAtPath(editor, normalizedMacro, { at: path });
      break;

    default:
      unreachableCaseLog(macroType);
      return;
  }
};

export const removeMacroPlaceholderAtPath = (editor: Editor, path: Path): void => {
  const parentPath = Path.parent(path);

  Editor.withoutNormalizing(editor, () => {
    Transforms.removeNodes(editor, { at: path });

    const parentNode = Node.get(editor, parentPath);
    if (Element.isElement(parentNode) && parentNode.children.length === 0) {
      Transforms.insertNodes(editor, { text: '' }, { at: [...parentPath, 0] });
    }
  });
};

export const buildMacroNameIndex = (
  macros: ReadonlyArray<MacroNameLookupEntry>
): Map<string, string> => {
  const macroNamesBySmid = new Map<string, string>();

  const visitMacros = (entries: ReadonlyArray<MacroNameLookupEntry>): void => {
    for (const macro of entries) {
      macroNamesBySmid.set(macro.smid, macro.name);

      if (macro.nestedMacros != null && macro.nestedMacros.length > 0) {
        visitMacros(macro.nestedMacros);
      }
    }
  };

  visitMacros(macros);
  return macroNamesBySmid;
};

export const applyMacroPlaceholderNames = (
  content: SlateContent,
  macroNamesBySmid: ReadonlyMap<string, string>
): SlateContent => {
  const hydratedContent = clone(content);

  walkSlateContent((node: Node) => {
    if (!Element.isElement(node) || node.type !== MACRO_PLACEHOLDER_PLUGIN_ID) {
      return;
    }

    const macroPlaceholderNode = node as {
      referenceID: string;
      name?: string;
    };
    const maybeName = macroNamesBySmid.get(macroPlaceholderNode.referenceID);
    if (maybeName == null) return;

    macroPlaceholderNode.name = maybeName;
  })(hydratedContent);

  return hydratedContent;
};

export const hydrateMacroPlaceholderNames = (
  content: SlateContent,
  macros: ReadonlyArray<MacroNameLookupEntry>
): SlateContent => applyMacroPlaceholderNames(content, buildMacroNameIndex(macros));

export const getMacroPlaceholderDisplayName = (
  element: Pick<MacroPlaceholderPluginElement, 'name' | 'referenceID' | 'children'>
): string => {
  const name = element.name?.trim();
  if (name != null && name.length > 0) return name;

  const childText = Node.string(element as MacroPlaceholderPluginElement).trim();
  if (childText.length > 0) return childText;

  return element.referenceID;
};
